import React, { useEffect, useState } from 'react';
import BemCn from 'bem-cn';
import {
    FacebookIcon,
    FacebookMessengerIcon,
    LineIcon,
    LinkedinIcon,
    PinterestIcon,
    RedditIcon,
    TelegramIcon,
    TumblrIcon,
    WhatsappIcon,
    XIcon,
} from 'react-share';
import CookieService from '../../services/cookie.service';
import { FB_APP_ID } from '../../consts/env.consts';
import { UTM_MEDIUM } from '../../consts/spot.consts';
import useMobileScreen from '../../services/hooks/useMobileScreen';
import { COOKIE_PARAM_NAME } from '../../consts/cookies.consts';
import { copyTextToClipboard } from '../../helpers/clipboard';
import ModalMobile from '../modal-mobile';
import SniffButton from '../sniff-button';
import SniffInput from '../sniff-input';
import './index.scss';

const useGetShareListingsLink = require('../../services/hooks/useGetShareListingsLink');

const b = BemCn('spot-share-button');

const SF_FENCE_OPTIONS = [
    {
        value: undefined,
        label: 'Any height',
    },
    {
        value: 'FT_3',
        label: '3+ ft',
    },
    {
        value: 'FT_4',
        label: '4+ ft',
    },
    {
        value: 'FT_5',
        label: '5+ ft',
    },
    {
        value: 'FT_6_PLUS',
        label: '6+ ft',
    },
];

const getSearchFiltersForServer = (filters) => {
    if (!filters) {
        return {};
    }

    let tmp = {};
    tmp.availableDates = filters.availableDates || undefined;
    tmp.availableTimes = filters.availableTimes || undefined;
    tmp.enclosureType = filters.enclosureType || undefined;
    tmp.fenceHeight = filters.fencing || SF_FENCE_OPTIONS[0].value || undefined;
    tmp.minimumSize = filters.minimumSize || undefined;
    tmp.reviewRating = filters.reviewRating || undefined;
    tmp.advanceNotice = filters.advanceNotice || undefined;
    tmp.distance = filters.distance || undefined;
    tmp.dogsPresent = filters.dogsPresent ? 'NO' : undefined;
    tmp.peoplePresent = filters.peoplePresent ? 'NO' : undefined;
    tmp.domesticAnimalsPresent = filters.domesticAnimalsPresent ? 'NO' : undefined;
    tmp.topSpots = filters.topSpots || undefined;
    tmp.newSpots = filters.newSpots || undefined;
    tmp.cleanerSpots = filters.cleanerSpots || undefined;
    tmp.sniffpassDiscount = filters.sniffpassDiscount || undefined;
    tmp.amenities = filters.amenities?.length > 0 ? filters.amenities : undefined;
    tmp.maximumDogsAllowed = filters.maximumDogsAllowed || undefined;
    tmp.minimumPrice = filters.minimumPrice || undefined;
    tmp.maximumPrice = filters.maximumPrice || undefined;
    tmp.sortBy = filters.sortBy || undefined;
    tmp.showPublicSpots = filters.showPublicSpots || undefined;
    tmp.showPrivateSpots = filters.showPrivateSpots || undefined;

    return tmp;
};

const getUrlSearchVars = () => {
    const searchCookie = CookieService.get(COOKIE_PARAM_NAME.SEARCH_FILTERS);
    const searchAvaCookie = CookieService.get(COOKIE_PARAM_NAME.SEARCH_FILTERS_AVAILABILITY);
    const searchLocCookie = CookieService.get(COOKIE_PARAM_NAME.SEARCH_FILTERS_LOCATION);
    const searchMisCookie = CookieService.get(COOKIE_PARAM_NAME.SEARCH_FILTERS_MISC);

    let filters = JSON.parse(searchCookie)?.filters || undefined;

    if (filters) {
        filters.availableDates = JSON.parse(searchAvaCookie)?.availableDates || undefined;
        filters.availableTimes = JSON.parse(searchAvaCookie)?.availableTimes || undefined;
        filters.dogsPresent = filters?.dogsPresent ? 'NO' : undefined;
        filters.peoplePresent = filters?.peoplePresent ? 'NO' : undefined;
        filters.domesticAnimalsPresent = filters?.domesticAnimalsPresent ? 'NO' : undefined;
        filters.address = undefined;
    }

    filters = getSearchFiltersForServer(filters);

    const center = JSON.parse(searchMisCookie)?.center || undefined;
    const bounds = JSON.parse(searchMisCookie)?.bounds || undefined;
    const hasPhoto = JSON.parse(searchMisCookie)?.hasPhoto || undefined;
    const location = JSON.parse(searchLocCookie)?.address || undefined;
    const utmSource = 'search_share';
    const utmMedium = 'web';

    return { filters, center, bounds, location, hasPhoto, utmSource, utmMedium };
};

const RenderCopy = ({ isMobile, copied, copyLink, pageUrl }) => {
    const buttonText = copied ? 'Copied!' : isMobile ? 'Copy link' : 'Copy';

    return (
        <>
            <p className="snif-p snif-medium mb-1 mt-2 mt-md-0">Copy link URL</p>
            <div className="share-box-copy mb-0 mb-md-4">
                <SniffInput type="text" value={pageUrl} readOnly />
                <SniffButton className="share-box-copy__action" color={copied ? 'secondary' : 'primary'} size="lg" block="mobile" onClick={copyLink}>
                    {buttonText}
                </SniffButton>
            </div>
        </>
    );
};

const SharePage = ({ TriggerComp, shareModal, toggleShareModal, title = 'Share this spot!', shareBlog, shareSearch }) => {
    const isMobile = useMobileScreen();
    const [copied, setCopied] = useState(false);
    const { getListingsLink } = !shareSearch || typeof window === 'undefined' ? {} : useGetShareListingsLink.default();
    const addedUtmBlog = '?utm_source=blogshare&utm_medium=share_button&utm_medium=web&utm_campaign={blog_share}';
    const addedUtmGeneral = '?utm_medium=share_button&utm_medium=web';
    const baseUrl = typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host + window.location.pathname : '';
    const [pageUrl, setPageUrl] = useState(
        encodeURI(`${baseUrl}${!shareSearch && !shareBlog ? addedUtmGeneral : ''}${shareBlog ? addedUtmBlog : ''}`)
    );

    const copyLink = async () => {
        try {
            let shareLink = pageUrl;

            if (shareSearch) {
                const variables = getUrlSearchVars();
                const { data } = await getListingsLink({ variables });
                shareLink = data.shareListingsLink;
            }

            await copyTextToClipboard(shareLink);
            setCopied(true);
        } catch (error) {
            console.warn(error);
        }
    };

    const mobileShare = async () => {
        try {
            let shareLink = pageUrl;

            if (shareSearch) {
                const variables = getUrlSearchVars();
                const { data } = await getListingsLink({ variables });
                shareLink = data.shareListingsLink;
            }

            const shareData = { url: shareLink };
            await navigator.share(shareData);
        } catch (err) {
            console.warn(err);
            if (err?.name === 'NotAllowedError') {
                toggleShareModal();
            }
        }
    };

    const desktopShare = async (medium) => {
        try {
            let shareSpotLink = encodeURIComponent(`${baseUrl}${shareBlog ? addedUtmBlog : ''}`);

            if (shareSearch) {
                const variables = getUrlSearchVars();
                const { data } = await getListingsLink({ variables });
                shareSpotLink = encodeURIComponent(data.shareListingsLink);
            }

            let shareLink = '';
            if (medium === UTM_MEDIUM.FACEBOOK.type) {
                shareLink = UTM_MEDIUM.FACEBOOK.link + shareSpotLink;
            } else if (medium === UTM_MEDIUM.TWITTER.type) {
                shareLink = UTM_MEDIUM.TWITTER.link + shareSpotLink;
            } else if (medium === UTM_MEDIUM.PINTEREST.type) {
                shareLink = UTM_MEDIUM.PINTEREST.link + shareSpotLink;
            } else if (medium === UTM_MEDIUM.LINKEDIN.type) {
                shareLink = UTM_MEDIUM.LINKEDIN.link + shareSpotLink;
            } else if (medium === UTM_MEDIUM.TUMBLR.type) {
                shareLink = UTM_MEDIUM.TUMBLR.link + shareSpotLink;
            } else if (medium === UTM_MEDIUM.REDDIT.type) {
                shareLink = UTM_MEDIUM.REDDIT.link + shareSpotLink;
            } else if (medium === UTM_MEDIUM.WHATSAPP.type) {
                shareLink = UTM_MEDIUM.WHATSAPP.link + shareSpotLink;
            } else if (medium === UTM_MEDIUM.MESSENGER.type) {
                shareLink = UTM_MEDIUM.MESSENGER.link + shareSpotLink + `&app_id=${FB_APP_ID}` + `&redirect_uri=${shareSpotLink}`;
            } else if (medium === UTM_MEDIUM.TELEGRAM.type) {
                shareLink = UTM_MEDIUM.TELEGRAM.link + shareSpotLink;
            } else if (medium === UTM_MEDIUM.LINE.type) {
                shareLink = UTM_MEDIUM.LINE.link + shareSpotLink;
            }
            const width = 800;
            const height = 720;
            const left = (screen.width - width) / 2;
            const top = (screen.height - height) / 3;
            window.open(shareLink, '_blank', `location=yes,width=${width},height=${height},left=${left},top=${top},scrollbars=yes,status=yes`);
        } catch (error) {
            console.warn(error);
        }
    };

    useEffect(() => {
        if (!shareModal) {
            setTimeout(() => {
                setCopied(false);
            }, 500);
            return;
        }

        const shareListings = async () => {
            const variables = getUrlSearchVars();
            const { data } = await getListingsLink({ variables });
            setPageUrl(data.shareListingsLink);
        };

        if (shareSearch) {
            shareListings();
        }
    }, [shareModal]);

    return (
        <div>
            <div onClick={isMobile ? mobileShare : toggleShareModal}>
                <TriggerComp />
            </div>
            <div className={b('box')}>
                <ModalMobile isOpen={shareModal} toggle={toggleShareModal} title={title} className="share-modal">
                    <p className="snif-p snif-medium mb-2">Social Media</p>
                    <div className="share-box mb-2">
                        <div className="react-share__ShareButton" onClick={() => desktopShare(UTM_MEDIUM.FACEBOOK.type)}>
                            <FacebookIcon size={40} round />
                        </div>
                        <div className="react-share__ShareButton" onClick={() => desktopShare(UTM_MEDIUM.TWITTER.type)}>
                            <XIcon size={40} round />
                        </div>
                        <div className="react-share__ShareButton" onClick={() => desktopShare(UTM_MEDIUM.PINTEREST.type)}>
                            <PinterestIcon size={40} round />
                        </div>
                        <div className="react-share__ShareButton" onClick={() => desktopShare(UTM_MEDIUM.LINKEDIN.type)}>
                            <LinkedinIcon size={40} round />
                        </div>
                        <div className="react-share__ShareButton" onClick={() => desktopShare(UTM_MEDIUM.TUMBLR.type)}>
                            <TumblrIcon size={40} round />
                        </div>
                        <div className="react-share__ShareButton" onClick={() => desktopShare(UTM_MEDIUM.REDDIT.type)}>
                            <RedditIcon size={40} round />
                        </div>
                    </div>
                    <div className="d-none d-md-block">
                        <RenderCopy {...{ isMobile, copied, copyLink, pageUrl }} />
                    </div>
                    <p className="snif-p snif-medium mb-2">Messaging</p>
                    <div className="share-box">
                        <div className="react-share__ShareButton" onClick={() => desktopShare(UTM_MEDIUM.WHATSAPP.type)}>
                            <WhatsappIcon size={40} round />
                        </div>
                        <div className="react-share__ShareButton" onClick={() => desktopShare(UTM_MEDIUM.MESSENGER.type)}>
                            <FacebookMessengerIcon size={40} round />
                        </div>
                        <div className="react-share__ShareButton" onClick={() => desktopShare(UTM_MEDIUM.TELEGRAM.type)}>
                            <TelegramIcon size={40} round />
                        </div>
                        <div className="react-share__ShareButton" onClick={() => desktopShare(UTM_MEDIUM.LINE.type)}>
                            <LineIcon size={40} round />
                        </div>
                    </div>
                    <div className="d-block d-md-none">
                        <RenderCopy {...{ isMobile, copied, copyLink, pageUrl }} />
                    </div>
                </ModalMobile>
            </div>
        </div>
    );
};

export default SharePage;
